/**
 * Resuelve un wizard preset (hints por nombre) contra el contexto del modelo activo.
 * Emite state con rows/values (dataset) + compat legacy.
 */

function emptyRef(overrides = {}) {
  return {
    tableId: '',
    column: '',
    jsonPath: '',
    aggregation: 'sum',
    dateBucket: 'raw',
    ...overrides,
  };
}

function emptyFilter(overrides = {}) {
  return {
    tableId: '',
    column: '',
    jsonPath: '',
    layer: 'universe',
    dynamic: false,
    operator: 'eq',
    value: '',
    paramName: '',
    previewValue: '',
    ...overrides,
  };
}

function norm(s) {
  return String(s || '').toLowerCase();
}

function tableHaystack(t) {
  return norm(`${t.id || ''} ${t.label || ''} ${t.name || ''}`);
}

function findTable(tables, hints = []) {
  if (!tables?.length) return null;
  if (!hints?.length) return tables[0];
  let best = null;
  let bestScore = -1;
  for (const t of tables) {
    const hay = tableHaystack(t);
    let score = 0;
    for (const h of hints) {
      const k = norm(h);
      if (!k) continue;
      if (hay.includes(k)) score += k.length > 3 ? 3 : 2;
    }
    if (score > bestScore) {
      bestScore = score;
      best = t;
    }
  }
  return bestScore > 0 ? best : tables[0];
}

function findColumn(table, roles = [], opts = {}) {
  const cols = table?.columns || [];
  if (!cols.length) return null;
  const preferTypes = opts.logicalTypes || [];
  let best = null;
  let bestScore = -1;
  for (const c of cols) {
    const n = norm(c.name);
    let score = 0;
    for (const r of roles) {
      const k = norm(r);
      if (!k) continue;
      if (n === k) score += 10;
      else if (n.includes(k) || k.includes(n)) score += 5;
    }
    if (preferTypes.length && preferTypes.includes(c.logicalType)) score += 2;
    if (opts.preferJson && (c.logicalType === 'json' || c.supportsJsonPath)) score += 3;
    if (opts.preferPk && c.isPrimaryKey) score += 2;
    if (score > bestScore) {
      bestScore = score;
      best = c;
    }
  }
  if (bestScore > 0) return best;
  if (preferTypes.length) {
    const byType = cols.find((c) => preferTypes.includes(c.logicalType));
    if (byType) return byType;
  }
  return cols[0];
}

function resolveField(tables, hint, defaults = {}) {
  if (!hint) return emptyRef(defaults);
  const table = findTable(tables, hint.tableHints);
  if (!table) return emptyRef(defaults);
  const col = findColumn(table, hint.roles || [], {
    logicalTypes: hint.logicalTypes,
    preferJson: hint.preferJson,
    preferPk: hint.preferPk,
  });
  return {
    tableId: table.id,
    column: col?.name || '',
    jsonPath: hint.jsonPath || '',
    aggregation: hint.aggregation != null ? hint.aggregation : defaults.aggregation || 'none',
    dateBucket: hint.dateBucket || defaults.dateBucket || 'raw',
  };
}

function resolveFilterField(tables, fh, wizard) {
  return resolveField(
    tables,
    {
      roles: fh.roles,
      tableHints: fh.tableHints || wizard.tableHints,
      jsonPath: fh.jsonPath,
      logicalTypes: fh.logicalTypes,
    },
    { aggregation: 'none' }
  );
}

function toDatasetShape(state) {
  let rows = Array.isArray(state.rows) ? state.rows : null;
  let values = Array.isArray(state.values) ? state.values : null;

  if (!rows && Array.isArray(state.tableColumns)) {
    rows = state.tableColumns
      .filter((c) => !c.aggregation || c.aggregation === 'none')
      .map((c) => emptyRef({ ...c, aggregation: 'none' }));
    values = state.tableColumns
      .filter((c) => c.aggregation && c.aggregation !== 'none')
      .map((c) => emptyRef({ ...c, aggregation: c.aggregation || 'sum' }));
  }
  if (!rows && state.dimension?.column) {
    rows = [emptyRef({ ...state.dimension, aggregation: 'none' })];
  }
  if (!values && state.measure?.column) {
    values = [emptyRef({ ...state.measure, aggregation: state.measure.aggregation || 'sum' })];
  }
  if (!rows?.length) rows = [emptyRef({ aggregation: 'none' })];
  if (!values) values = [];

  const viz = state.visualization || {
    chartType: state.chartType || 'table',
    xField: undefined,
    yField: undefined,
  };

  return {
    ...state,
    kind: 'dataset',
    rows,
    values,
    visualization: viz,
    chartType: viz.chartType || state.chartType || 'table',
  };
}

/**
 * Carga un ejemplo guardado con wizardState exacto (sin heurísticas).
 */
export function applyWizardState(example) {
  const ws = example?.wizardState || {};
  const base = {
    chartType: ws.visualization?.chartType || ws.chartType || example?.suggestedChartType || 'table',
    title: ws.title || example?.title || '',
    baseTableId: ws.baseTableId || '',
    rowLimit: ws.rowLimit != null ? ws.rowLimit : 100,
    orderByKind: ws.orderByKind || 'column',
    orderDirection: ws.orderDirection || 'desc',
    orderColumnIndex: ws.orderColumnIndex != null ? ws.orderColumnIndex : 0,
    useSeries: Boolean(ws.useSeries),
    dimension: ws.dimension || emptyRef({ aggregation: 'none', dateBucket: 'raw' }),
    measure: ws.measure || emptyRef({ aggregation: 'sum' }),
    series: ws.series || emptyRef({ aggregation: 'none' }),
    tableColumns:
      Array.isArray(ws.tableColumns) && ws.tableColumns.some((c) => c?.tableId && c?.column)
        ? ws.tableColumns
        : undefined,
    rows: Array.isArray(ws.rows) ? ws.rows : undefined,
    values: Array.isArray(ws.values) ? ws.values : undefined,
    filters: Array.isArray(ws.filters) ? ws.filters : [],
    visualization: ws.visualization || { chartType: ws.chartType || 'table' },
    loadedExampleId: example?.id || null,
    loadedExampleTitle: example?.title || null,
    loadNotes: '',
  };
  return {
    state: toDatasetShape(base),
    warnings: example?.database
      ? [`Guardado para BD «${example.database}». Si cambiaste de modelo, revisa tablas/columnas.`]
      : [],
    matched: {},
  };
}

/**
 * @returns {{ state: object, warnings: string[], matched: object }}
 */
export function resolveExampleWizard(example, context) {
  if (
    example?.wizardState &&
    (example.wizardState.kind === 'dataset' ||
      example.wizardState.rows ||
      example.wizardState.values ||
      example.wizardState.chartType ||
      example.wizardState.visualization)
  ) {
    return applyWizardState(example);
  }

  const wizard = example?.wizard || {};
  const tables = context?.tables || [];
  const warnings = [];
  const matched = { tables: [], columns: [] };

  if (!tables.length) {
    warnings.push('No hay tablas en el modelo activo. Activa el modelo y vuelve a cargar el ejemplo.');
  }

  const chartType = wizard.chartType || example?.suggestedChartType || 'table';
  const primaryTable = findTable(tables, wizard.tableHints || []);
  if (primaryTable) matched.tables.push(primaryTable.id);

  const state = {
    chartType,
    title: wizard.title || example?.title || '',
    baseTableId: primaryTable?.id || '',
    rowLimit: wizard.rowLimit != null ? wizard.rowLimit : chartType === 'table' ? 10 : 500,
    orderByKind: wizard.orderByKind || (chartType === 'table' ? 'column' : 'measure'),
    orderDirection: wizard.orderDirection || 'desc',
    orderColumnIndex: 0,
    useSeries: Boolean(wizard.useSeries),
    dimension: emptyRef({ aggregation: 'none', dateBucket: 'raw' }),
    measure: emptyRef({ aggregation: 'sum' }),
    series: emptyRef({ aggregation: 'none' }),
    tableColumns: [emptyRef({ aggregation: 'none' })],
    rows: undefined,
    values: undefined,
    filters: [],
    visualization: { chartType },
    loadedExampleId: example?.id || null,
    loadedExampleTitle: example?.title || null,
    loadNotes: wizard.notes || '',
  };

  if (wizard.rowHints?.length || wizard.valueHints?.length) {
    state.rows = (wizard.rowHints || []).map((h) => {
      const field = resolveField(
        tables,
        { ...h, tableHints: h.tableHints || wizard.tableHints },
        { aggregation: 'none', dateBucket: h.dateBucket || 'raw' }
      );
      if (field.column) matched.columns.push(`${field.tableId}.${field.column}`);
      else warnings.push(`Fila: no se encontró columna ${(h.roles || []).join(', ')}`);
      return field;
    });
    state.values = (wizard.valueHints || []).map((h) => {
      const field = resolveField(
        tables,
        { ...h, tableHints: h.tableHints || wizard.tableHints },
        { aggregation: h.aggregation || 'sum' }
      );
      if (field.column) matched.columns.push(`${field.tableId}.${field.column}`);
      else warnings.push(`Valor: no se encontró columna ${(h.roles || []).join(', ')}`);
      return field;
    });
    if (!state.rows.length) state.rows = [emptyRef({ aggregation: 'none' })];
    if (wizard.orderColumnHint) {
      const orderField = resolveField(
        tables,
        { ...wizard.orderColumnHint, tableHints: wizard.orderColumnHint.tableHints || wizard.tableHints },
        { aggregation: 'none' }
      );
      const allCols = [...state.rows, ...state.values];
      const idx = allCols.findIndex(
        (c) => c.tableId === orderField.tableId && c.column === orderField.column
      );
      state.orderColumnIndex = idx >= 0 ? idx : 0;
    }
    if (wizard.visualization) {
      state.visualization = { chartType, ...wizard.visualization };
    }
  } else if (chartType === 'table') {
    const hints = wizard.tableColumnHints?.length
      ? wizard.tableColumnHints
      : [{ roles: ['name'] }, { roles: ['value', 'data'] }];
    state.tableColumns = hints.map((h) => {
      const field = resolveField(
        tables,
        { ...h, tableHints: h.tableHints || wizard.tableHints },
        { aggregation: h.aggregation || 'none' }
      );
      if (field.column) matched.columns.push(`${field.tableId}.${field.column}`);
      else warnings.push(`No se encontró columna para roles: ${(h.roles || []).join(', ')}`);
      return field;
    });
    if (wizard.orderColumnHint) {
      const orderField = resolveField(
        tables,
        { ...wizard.orderColumnHint, tableHints: wizard.orderColumnHint.tableHints || wizard.tableHints },
        { aggregation: 'none' }
      );
      const idx = state.tableColumns.findIndex(
        (c) => c.tableId === orderField.tableId && c.column === orderField.column
      );
      state.orderColumnIndex = idx >= 0 ? idx : 0;
    }
  } else if (chartType === 'kpi') {
    state.measure = resolveField(
      tables,
      {
        ...(wizard.measureHint || { roles: ['value', 'id'], aggregation: 'count' }),
        tableHints: wizard.measureHint?.tableHints || wizard.tableHints,
      },
      { aggregation: 'count' }
    );
  } else {
    state.dimension = resolveField(
      tables,
      {
        ...(wizard.dimensionHint || { roles: ['name'], dateBucket: 'raw' }),
        tableHints: wizard.dimensionHint?.tableHints || wizard.tableHints,
      },
      { aggregation: 'none', dateBucket: 'raw' }
    );
    state.measure = resolveField(
      tables,
      {
        ...(wizard.measureHint || { roles: ['id', 'value'], aggregation: 'count' }),
        tableHints: wizard.measureHint?.tableHints || wizard.tableHints,
      },
      { aggregation: 'count' }
    );
  }

  state.filters = (wizard.filterHints || []).map((fh) => {
    const field = resolveFilterField(tables, fh, wizard);
    const layer = fh.layer || 'universe';
    const isSel = layer === 'selection';
    if (!field.column) {
      warnings.push(`Filtro (${layer}): no se encontró columna ${(fh.roles || []).join(', ')}`);
    }
    return emptyFilter({
      tableId: field.tableId,
      column: field.column,
      jsonPath: field.jsonPath || fh.jsonPath || '',
      layer,
      dynamic: isSel,
      operator: fh.operator || 'eq',
      value: isSel ? '' : fh.value ?? '',
      paramName: isSel ? fh.paramName || 'Param' : '',
      previewValue: isSel ? fh.previewValue ?? '' : '',
    });
  });

  if (wizard.notes) warnings.push(wizard.notes);

  return { state: toDatasetShape(state), warnings, matched };
}

export { emptyRef, emptyFilter };
